import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import seoConfig from "@/seo/seoRoutes.json";

const SITE_URL = seoConfig.siteUrl;
const defaultSeo = {
  title: "Jyvis Assurance – Courtier en assurances | Île-de-France & Guadeloupe",
  description: "Jyvis Assurance, votre courtier indépendant en Île-de-France et Guadeloupe.",
  keywords: "courtier assurance, assurance, devis gratuit"
};

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

export default function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const seo = seoConfig.routes[path] || defaultSeo;
    const url = path === "/" ? SITE_URL : `${SITE_URL}${path}`;

    document.title = seo.title;
    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('meta[name="keywords"]', "content", seo.keywords);
    setMeta('meta[name="robots"]', "content", seo.noindex ? "noindex, nofollow" : "index, follow");
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
    setMeta('meta[name="twitter:url"]', "content", url);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [location.pathname]);

  return null;
}

/**
 * Structured Data for Local Business (JSON-LD)
 */
export function LocalBusinessSchema() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "InsuranceAgency",
      "name": "JyVis Assurances",
      "legalName": "LS ASSUR",
      "description": "Courtier indépendant en assurances en Île-de-France et Guadeloupe",
      "url": "https://www.jyvis-assurances.com",
      "logo": "https://www.jyvis-assurances.com/logo.png",
      "telephone": "+33148209827",
      "email": "contact@jyvis-assurances.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "47 rue Jean Jaurès",
        "addressLocality": "Saint-Denis",
        "postalCode": "93200",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 48.9362,
        "longitude": 2.3574
      },
      "areaServed": [
        { "@type": "AdministrativeArea", "name": "Seine-Saint-Denis (93)" },
        { "@type": "AdministrativeArea", "name": "Hauts-de-Seine (92)" },
        { "@type": "AdministrativeArea", "name": "Val-de-Marne (94)" },
        { "@type": "AdministrativeArea", "name": "Val-d'Oise (95)" },
        { "@type": "AdministrativeArea", "name": "Paris (75)" },
        { "@type": "AdministrativeArea", "name": "Guadeloupe (971)" }
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "sameAs": [
        "https://www.instagram.com/jyvis.assurances/",
        "https://www.facebook.com/profile.php?id=100078545120236",
        "https://www.linkedin.com/in/jyvis-assurances-206017357/",
        "https://www.youtube.com/@JyvisAssurances",
        "https://www.tiktok.com/@jyvis.assurances"
      ],
      "priceRange": "€€"
    };
    
    let script = document.getElementById("local-business-schema");
    if (!script) {
      script = document.createElement("script");
      script.id = "local-business-schema";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    
    return () => {
      const existingScript = document.getElementById("local-business-schema");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);
  
  return null;
}

/**
 * Breadcrumb Schema for SEO
 */
export function BreadcrumbSchema({ items }) {
  useEffect(() => {
    if (!items || items.length === 0) return;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `https://www.jyvis-assurances.com${item.path}`
      }))
    };
    
    let script = document.getElementById("breadcrumb-schema");
    if (!script) {
      script = document.createElement("script");
      script.id = "breadcrumb-schema";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    
    return () => {
      const existingScript = document.getElementById("breadcrumb-schema");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [items]);
  
  return null;
}

/**
 * FAQ Schema for SEO
 */
export function FAQSchema({ faqs }) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
    
    let script = document.getElementById("faq-schema");
    if (!script) {
      script = document.createElement("script");
      script.id = "faq-schema";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    
    return () => {
      const existingScript = document.getElementById("faq-schema");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [faqs]);
  
  return null;
}
